import React from 'react';
import { initReactI18next } from 'react-i18next';
import { hydrateRoot } from 'react-dom/client';
import axios from 'axios';
import i18n from 'i18next';
import reactGuard from 'react-guard';
import { getCompositionByElementId } from '../../lib/BrowserServerCommon/compositionHandler';
import LoadingFeedback from '../../components/atoms/LoadingFeedback/LoadingFeedback';
import ErrorDisabledJavascript from '../../_lib/components/ErrorDisabledJavascript/ErrorDisabledJavascript';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import LiveRegionGlobal from '../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { ExternalLinkDescription } from '../../components/atoms/ExternalLinkDescription/ExternalLinkDescription';
import { i18nOptions } from '../../translation/i18n';
import { Composition } from '../../types/api/Composition';
import { RoutedPage, RoutedPageProps } from './RoutedPage';
import { StatusInfoErrorBoundary } from '../../_lib/components/StatusInfo';
import { SocketManager } from '../../_lib/WebSocketManager';
import { globals } from '../../lib/EnvironmentDetection';
import { windowLogger } from '../../_lib/Logger/windowLogger';

type ApplicationProps = RoutedPageProps;
/**
 * Patches React by creating try/catch block around every component.
 * Renders ErrorInfo component instead of white screen of death
 */
reactGuard(React, (err, componentInfo) => {
  windowLogger.error(err.stack, '\n', componentInfo);
  return <StatusInfoErrorBoundary />;
});

/**
 * This Component is the Root-Component of the client side.
 * It will be rendered in browser and hydrates the Webapp Component rendered by the server.
 */
export function Webapp({ composition }: ApplicationProps) {
  return (
    <div>
      <ErrorDisabledJavascript />
      <LoadingFeedback />
      <SocketManager endpoint={composition.websocketEndpoint} loggedIn={!!composition.userInfo?.loggedIn}>
        <RoutedPage composition={composition} />
      </SocketManager>
      <LiveRegionGlobal />
      <ExternalLinkDescription />
    </div>
  );
}

export const initClientBundle = async () => {
  const defaultLang = 'de';
  const translations = await axios.get(`/assets/translations/${defaultLang}.json?${globals.bundleHash}`);
  const composition = getCompositionByElementId(GlobalDocumentIds.ssrCompositionJson) as Composition;

  if (!i18n.isInitialized && translations) {
    await i18n.use(initReactI18next).init(i18nOptions('de', { [defaultLang]: translations.data }));
  }

  const rootElement = document.getElementById(GlobalDocumentIds.root);
  if (rootElement) {
    hydrateRoot(
      rootElement,
      <React.StrictMode>
        <Webapp composition={composition} />
      </React.StrictMode>,
    );
  }
};

export default initClientBundle();
